import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@ngneat/transloco';
import { catchError, map, of } from 'rxjs';
import { rxResource } from '@angular/core/rxjs-interop';
import { AuditService } from '../../core/services/audit.service';
import { AuthService } from '../../core/services/auth.service';
import { LoyaltyService } from '../../core/services/loyalty.service';
import { OrderService } from '../../core/services/order.service';
import { PremiumService } from '../../loyalty/premium/premium.service';
import { StoreIdService } from '../../core/services/store-id.service';

type SavingsValue = { savings: number; planName: string };

@Component({
  selector: 'cwo-cart-sub-promo',
  imports: [CommonModule, FormsModule, TranslocoDirective],
  templateUrl: './cart-sub-promo.component.html',
  styleUrls: ['./cart-sub-promo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartSubPromoComponent {
  private readonly authService = inject(AuthService);
  private readonly loyaltyService = inject(LoyaltyService);
  private readonly orderService = inject(OrderService);
  private readonly auditService = inject(AuditService);
  private readonly premiumService = inject(PremiumService);
  private readonly storeIdService = inject(StoreIdService);
  private readonly router = inject(Router);

  readonly isMultiplePlans = computed(
    () => this.premiumService.subscriptions().length > 1,
  );

  private readonly isNonSubscribedUser = computed(() => !this.premiumService.hasActiveSubscription());
  private readonly orderSubTotal = computed(() => this.orderService.order()?.subTotal);

  private readonly savingsResource = rxResource<
    SavingsValue,
    number | undefined
  >({
    params: () =>
      this.isNonSubscribedUser() ? this.orderSubTotal() : undefined,

    defaultValue: { savings: 0, planName: '' },

    stream: ({ params }) => {
      if (params === undefined) return of({ savings: 0, planName: '' });

      return this.loyaltyService.getUpgradedSubscriptionSavings().pipe(
        map((data) => ({
          savings: data?.savings ?? 0,
          planName: data?.tierName ?? '',
        })),
        catchError((err) => {
          this.auditService.createAudit(
            () => `Failed to load subscription savings: ${err}`,
          );
          return of({ savings: 0, planName: '' });
        }),
      );
    },
  });

  readonly isLoading = this.savingsResource.isLoading;

  readonly savingsPlan = computed(() =>
    this.isNonSubscribedUser() ? this.savingsResource.value().savings : 0,
  );

  readonly planName = computed(() =>
    this.isNonSubscribedUser() ? this.savingsResource.value().planName : '',
  );

  readonly premiumRoute = this.router.createUrlTree(
    this.storeIdService.getRoutePathSegments(['premium']),
  );
}
